/**
 * Dates.
 *
 * The restaurant's day is a Bratislava day. Every formatter here pins
 * `Europe/Bratislava` explicitly rather than trusting the browser's zone — a
 * tablet left on UTC would otherwise show an order placed at 00:30 local as
 * belonging to the previous day, and the uzávierka would not balance.
 */

const RESTAURANT_TIME_ZONE = 'Europe/Bratislava'
const LOCALE = 'sk-SK'

const DATE_TIME_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: RESTAURANT_TIME_ZONE,
})

const TIME_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: RESTAURANT_TIME_ZONE,
})

const DAY_MONTH_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  timeZone: RESTAURANT_TIME_ZONE,
})

/**
 * The shape `/preorders` needs: which day of the week, and when.
 *
 * A pre-order is read as *is that tomorrow or Thursday?*, so the weekday earns its place there and nowhere
 * else. It lived on that page as a fourth `Intl.DateTimeFormat` with its own copy of the zone string; it is
 * here so the zone is declared once (§9.3).
 */
const WEEKDAY_TIME_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: RESTAURANT_TIME_ZONE,
})

/**
 * Legacy's `casenka-toggle.tsx` label: the weekday spelled out, then the date and time.
 *
 * Separate from `WEEKDAY_TIME_FORMAT`, which abbreviates (`po`) for a board listing many rows. The
 * builder echoes **one** time back for somebody to check, and *štvrtok* is harder to misread than *št*.
 */
const NAMED_DAY_TIME_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  weekday: 'long',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: RESTAURANT_TIME_ZONE,
})

const DAY_FORMAT = new Intl.DateTimeFormat(LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: RESTAURANT_TIME_ZONE,
})

function toDate(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value)
}

/** `27.07.2026, 14:35` — the legacy `formatDate`. */
export function formatDateTime(value: string | number | Date): string {
  return DATE_TIME_FORMAT.format(toDate(value))
}

/** `14:35` — the legacy `formatTime`. */
export function formatTime(value: string | number | Date): string {
  return TIME_FORMAT.format(toDate(value))
}

/** `27. 07. 2026` — the date without the time, for a row that shows both separately. */
export function formatDay(value: string | number | Date): string {
  return DAY_FORMAT.format(toDate(value))
}

/** `po 27. 07., 14:35` — a scheduled time, with the weekday, for the pre-order board. */
export function formatWeekdayTime(value: string | number | Date): string {
  return WEEKDAY_TIME_FORMAT.format(toDate(value))
}

/** `štvrtok 24. 12. 18:00` — a scheduled time with the day named, for the order builder's časenka. */
export function formatNamedDayTime(value: string | number | Date): string {
  return NAMED_DAY_TIME_FORMAT.format(toDate(value))
}

/** `27.07.` — the legacy `formatDateShort`. */
export function formatDayMonth(value: string | number | Date): string {
  return DAY_MONTH_FORMAT.format(toDate(value))
}

/**
 * The pieces of an instant, read in the restaurant's zone.
 *
 * Everything below it converts rather than formats, so it needs the numbers and not the string the
 * `format*` helpers return. `sv-SE` because its parts are plain zero-padded numerals.
 */
const PARTS_FORMAT = new Intl.DateTimeFormat('sv-SE', {
  timeZone: RESTAURANT_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

function partsAt(instantMs: number): (type: string) => number {
  const parts = PARTS_FORMAT.formatToParts(new Date(instantMs))
  return (type) => {
    const found = parts.find((part) => part.type === type)
    return found === undefined ? 0 : Number(found.value)
  }
}

/** How far the zone is from UTC at a given instant, in milliseconds. Negative east of Greenwich. */
function offsetAt(utcMs: number): number {
  const read = partsAt(utcMs)
  return (
    utcMs -
    Date.UTC(read('year'), read('month') - 1, read('day'), read('hour'), read('minute'), read('second'))
  )
}

/**
 * A Bratislava wall time — `2026-03-29`, `01:30` — as an instant, in milliseconds.
 *
 * The customer picks a wall time; `OrderCreationController.requestedTime()` parses an **instant**
 * (`OffsetDateTime.parse`) and `RestaurantAvailability` converts it back with
 * `atZoneSameInstant(BusinessDay.ZONE)`. A zone with summer time makes that a real conversion.
 *
 * **Legacy samples the offset at the wrong instant** (FB-C09): it reads the offset that applies at the
 * naive time *treated as UTC*, then adds it. On 29 March 2026, when the clocks go forward at 02:00
 * local, asking for 01:30 yields `2026-03-28T23:30Z` — an hour early, on the previous day — and 00:30
 * and 01:30 collapse onto one instant. Converging fixes it in a line: apply the offset, then re-read it
 * *at the instant you landed on*. One extra pass covers every real zone, because offsets move by at most
 * a couple of hours and never twice within one.
 *
 * `NaN` when the pieces do not parse — callers check that rather than sending an `Invalid Date` through
 * `toISOString()`, which throws.
 */
export function zonedWallTimeToUtcMs(date: string, time: string): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{1,2}:\d{2}$/.test(time)) return Number.NaN
  const naive = Date.parse(`${date}T${time.padStart(5, '0')}:00Z`)
  if (!Number.isFinite(naive)) return Number.NaN
  const firstPass = naive + offsetAt(naive)
  // Re-read the offset where we actually landed. On an ordinary day this changes nothing.
  return naive + offsetAt(firstPass)
}

/** The wall time in Bratislava, whatever the device's own clock is set to. */
export function zonedWallTime(instantMs: number): { day: number; minutes: number } {
  const read = partsAt(instantMs)
  const asUtc = Date.UTC(read('year'), read('month') - 1, read('day'), read('hour'), read('minute'))
  return { day: new Date(asUtc).getUTCDay(), minutes: read('hour') * 60 + read('minute') }
}

/**
 * `2026-08-02` — the calendar date **in the restaurant's zone**, for an `<input type="date">`.
 *
 * `toDateParam` reads the device's own calendar, which is right for a till standing in the restaurant
 * and wrong for anything else: a tablet left on UTC shows 1 August at half past midnight local, and a
 * časenka defaulted to that date is a day early. This asks the same zone every other conversion here
 * asks, so the default date and the instant it becomes agree.
 */
export function zonedDateParam(instantMs: number): string {
  const read = partsAt(instantMs)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${read('year')}-${pad(read('month'))}-${pad(read('day'))}`
}

export function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

// `endOfDay` was here and is deleted — §9.35, 2026-08-06. Correct, and simply never needed:
// `startOfDay` has thirteen callers and this had none, and nothing builds a day's end inline either.
// The symmetry is what kept it — a pair reads better than a half — but an exported function is a
// promise that something uses it, and this one had nobody to keep it to.

/**
 * `2026-07-27` from local calendar components.
 *
 * Not `toISOString()`: that converts to UTC first, so a Bratislava midnight
 * (UTC+1 in winter, +2 in summer) renders as the previous day and a "today"
 * query silently spans two calendar days server-side. Ported from the legacy
 * `toDateParam`, comment and all, because the comment is the reason.
 */
export function toDateParam(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// `effectiveDate` was here and is deleted — §9.35, 2026-08-06, and it was **wrong**.
//
// It read `order.scheduledAt ?? order.createdAt` under a docblock claiming to be §14.8's rule.
// The rule, as `EffectiveDate` builds it into every reporting query, is
// `CASE WHEN is_preorder = true AND scheduled_at IS NOT NULL THEN scheduled_at ELSE created_at END`
// — the preorder flag is half of it. So an order carrying a `scheduledAt` without being a preorder
// would have been attributed to the scheduled day here and to the day it was taken by the server,
// and the docblock's own warning applies to itself: getting it wrong moves revenue between days.
//
// Nothing called it, so nothing was ever misattributed. Measured before deleting: of 697 orders with
// a `scheduled_at`, **all** are preorders, so no row alive today distinguishes the two rules — the
// divergence was latent rather than live.
//
// The rule is where it belongs. The database applies it to every report, and the one frontend
// surface that needs its own copy — `wallet.ts`, keying a driver's day — has it correct, inline,
// beside a comment explaining why a preorder taken on Monday for Saturday is Saturday's work.

/** Minutes elapsed since a timestamp — the age badges on the boards. */
export function minutesSince(value: string | number | Date, now: number): number {
  return Math.floor((now - toDate(value).getTime()) / 60_000)
}

/** `7 min` / `1h 12m` — the elapsed label the kitchen and driver views show. */
export function formatElapsed(value: string | number | Date, now: number): string {
  const minutes = minutesSince(value, now)
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}
